import { useCallback } from "react";
import { Host_APIS } from "../../../libs/apis/host.api";
import { CommunityDTO } from "utils/helpers/models/shared/community.dto";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import { FilterDTO } from "utils/helpers/models/shared/filter.dto";
import useStore from "hooks/useStore";

const useCommunityHooks = () => {
  const { getUserId } = useStore();

  // Function to fetch communities data from API
  const getCommunities = useCallback(
    async (setData: Function, queryParams: any = {}) => {
      const response = await Host_APIS.getCommunities(queryParams);
      const { status = false, data = {} } = response || {};
      if (status) {
        const { content = [] } = data;
        setData(content.map((i) => new CommunityDTO(i)));
      }
    },
    []
  );

  const sendMessageToCommunity = useCallback(
    async (
      message: MessageDTO,
      messages: MessageDTO[],
      setMessages: (msgs: MessageDTO[]) => void
    ) => {
      // Avoid sending client-only fields like id/sentByMe/createdAt to API
      const payload: any = { ...message } as any;
      if (payload.id !== undefined) delete payload.id;
      if (payload.sentByMe !== undefined) delete payload.sentByMe;
      if (payload.createdAt !== undefined) delete payload.createdAt;

      const response = await Host_APIS.sendMessageToCommunity(payload);
      const { status = false, data = {} } = response || {};
      if (status) {
        // Update all messages to replace the temporary message with the server response
        let replaced = false;
        const updatedMessages = messages.map((msg) => {
          if (msg.id === message.id) {
            replaced = true;
            return new MessageDTO({ ...data, sentByMe: true });
          }
          return msg;
        });
        // If we didn't find the optimistic message for any reason, append the server one
        const finalMessages = replaced
          ? updatedMessages
          : [...messages, new MessageDTO({ ...data, sentByMe: true })];
        setMessages(finalMessages);
      }
      return response;
    },
    []
  );

  const getCommunityMessages = useCallback(
    async (
      queryParams: any,
      setData: Function,
      messages: MessageDTO[],
      setFilters: Function
    ) => {
      const response = await Host_APIS.getCommunityMessages(queryParams);
      const { status = false, data = {} } = response || {};
      if (status) {
        const { totalPages } = data;
        const myUserId = getUserId();
        const { content = [] } = data;
        if (setFilters)
          setFilters((filters: any) => new FilterDTO({ ...filters, totalPages }));
        const dataToSet = content.map(
          (i) => new MessageDTO({ ...i, sentByMe: i.senderId === myUserId })
        );
        setData(dataToSet);
      }
    },
    [getUserId]
  );

  return {
    getCommunities,
    sendMessageToCommunity,
    getCommunityMessages,
  };
};

export default useCommunityHooks;
